/**
 * Session 8 Block 5 · LLM-Setup Onboarding-Schritt.
 *
 * Two cards: Platform-KI (empfohlen) vs. eigener API-Key (BYO).
 * Ohne LLM-Wahl kein Fortschritt in Onboarding.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { CheckCircle2, Sparkles, Key } from "lucide-react";


export default function LLMSetupStep() {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [provider, setProvider] = useState("openai");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testOk, setTestOk] = useState(null);

  useEffect(() => {
    api.get("/tenant/llm-config").then((r) => setState(r.data))
       .catch(() => {});
  }, []);

  if (!state) return <div data-testid="llm-setup-step">Lade…</div>;

  const activatePlatform = async () => {
    setSaving(true);
    try {
      const r = await api.put("/tenant/llm-config",
                                { mode: "platform" });
      setState(r.data);
      toast.success("Plattform-KI aktiviert.");
      nav("/app/onboarding");
    } catch (e) {
      toast.error(e.response?.data?.detail?.error
                    || "Aktivierung fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const testKey = async () => {
    if (!apiKey) return;
    setTesting(true); setTestOk(null);
    try {
      const r = await api.post("/tenant/llm-config/test",
                                 { provider, api_key: apiKey });
      setTestOk(r.data.ok);
      toast[r.data.ok ? "success" : "error"](
        r.data.ok ? "Schlüssel gültig." : "Format ungültig.");
    } catch (e) {
      toast.error("Fehler beim Test.");
      setTestOk(false);
    } finally { setTesting(false); }
  };

  const saveOwnKey = async () => {
    if (!apiKey) return;
    setSaving(true);
    try {
      const r = await api.put("/tenant/llm-config",
                                { mode: "own_key", provider,
                                  api_key: apiKey });
      setState(r.data);
      // Immediate test → activate.
      await api.post("/tenant/llm-config/test",
                        { provider, api_key: apiKey });
      toast.success("Eigener API-Key gespeichert.");
      nav("/app/onboarding");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const isConfigured = state.configured && state.status === "active";

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6"
          data-testid="llm-setup-step">
      <div>
        <h1 className="text-3xl font-semibold">KI-Provider einrichten</h1>
        <p className="text-muted-foreground mt-2">
          Wähle wie du die KI-Funktionen von VIONA nutzen möchtest.
          Ohne KI-Provider können Markenprofile, Kampagnen-Briefings
          und Creative-Generierung nicht laufen.
        </p>
        {isConfigured && (
          <Badge className="mt-3 bg-emerald-100 text-emerald-800"
                  data-testid="llm-setup-active-badge">
            Aktuell aktiv: {state.mode === "platform"
              ? "Plattform-KI" : `Eigener Key (${state.provider})`}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Card A: Platform LLM */}
        <Card data-testid="llm-setup-card-platform">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Plattform-KI (empfohlen)
                </CardTitle>
                <CardDescription>
                  Nutze die zentrale KI-Infrastruktur von VIONA. Kosten
                  sind in deinem Abo enthalten. Ideal für Einsteiger.
                </CardDescription>
              </div>
              <Badge>Empfohlen</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {state.platform_available ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Dein Plan enthält monatliche KI-Aufrufe.
                  Upgrade jederzeit möglich.
                </p>
                <Button onClick={activatePlatform} disabled={saving}
                         className="w-full"
                         data-testid="llm-setup-activate-platform">
                  {saving ? "Aktiviere…" : "Aktivieren"}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Wird gerade vom Plattform-Betreiber eingerichtet.
                Bitte später erneut versuchen oder eigenen Key nutzen.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Card B: Own key */}
        <Card data-testid="llm-setup-card-own-key">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Eigener API-Key (Fortgeschritten)
            </CardTitle>
            <CardDescription>
              Trage deinen eigenen OpenAI-, Anthropic- oder Gemini-Key
              ein. Rechnung geht direkt an dich. Volle Kontrolle.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Provider</Label>
              <select className="w-full rounded border border-input
                                   bg-background px-3 py-2 text-sm"
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                        data-testid="llm-setup-provider-select">
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="gemini">Google Gemini</option>
              </select>
            </div>
            <div>
              <Label>API-Key</Label>
              <Input type="password" value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="sk-…"
                      data-testid="llm-setup-api-key-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={testKey}
                       disabled={testing || !apiKey}
                       data-testid="llm-setup-test-btn">
                {testing ? "Prüfe…" : "Verbindung testen"}
              </Button>
              <Button onClick={saveOwnKey}
                       disabled={saving || !apiKey}
                       data-testid="llm-setup-save-own-key">
                {saving ? "Speichere…" : "Speichern & Weiter"}
              </Button>
            </div>
            {testOk === true && (
              <div className="text-emerald-700 flex items-center gap-1
                              text-sm">
                <CheckCircle2 className="h-4 w-4" />
                Format gültig.
              </div>
            )}
            {testOk === false && (
              <div className="text-rose-700 text-sm">
                Format ungültig. Bitte prüfen.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
